import { MouseEvent, useState } from 'react';

import { Button, IconButton, Modal, ModalActions, ModalContent, ModalWrapper, Typography, useDeviceType } from '@braskai/ui-kit';

import { Close } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';

import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { BoosterType } from '@/shared/types/Billing';

import { useBoosterConfirmation } from '../../hooks/useBoosterConfirmation';
import { useBoosters } from '../../hooks/useBoosters';
import { useBoosterSeatsCounter } from '../../hooks/useBoosterSeatsCounter';
import { formatBoosterPrice } from '../../lib/formatBoosterPrice';
import { BoosterItem } from '../BoosterItem/BoosterItem';
import { BoosterSeatsCounter } from '../BoosterSeatsCounter/BoosterSeatsCounter';
import BoostersBanner from './assets/boosters-banner.png';
import { useIntroduceBoostersModalStore } from './hooks/useIntroduceBoostersModalStore';

const IntroduceBoostersModalComponent = () => {
  const onClose = useIntroduceBoostersModalStore((state) => state.close);
  const openIntroduceBoostersModal = useIntroduceBoostersModalStore((state) => state.open);
  const { allBoosters } = useBoosters();
  const { isMobile } = useDeviceType();

  const { decrement, increment, seatsCount } = useBoosterSeatsCounter(1);
  const { openBoosterConfirmation } = useBoosterConfirmation();

  const [selectedBoosters, setSelectedBoosters] = useState<Record<BoosterType, boolean>>({
    [BoosterType.SRT]: true,
    [BoosterType.TeamspaceSeat]: true,
    [BoosterType.TranslationPrompting]: true,
  });


  const toggleBooster = (id: BoosterType) => {
    setSelectedBoosters((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const incrementSeats = (e: MouseEvent) => {
    e.stopPropagation();
    increment();
  };

  const decrementSeats = (e: MouseEvent) => {
    e.stopPropagation();
    decrement();
  };

  const handleAddAndContinue = () => {
    MixpanelEvents.boostersContinueClick({
      boosters_prompting: selectedBoosters[BoosterType.TranslationPrompting],
      boosters_seats: selectedBoosters[BoosterType.TeamspaceSeat] ? seatsCount : 0,
      boosters_srt: selectedBoosters[BoosterType.SRT],
      source: 'boosters_general_modal',
    });
    onClose();

    openBoosterConfirmation({
      seatsCount,
      selectedBoosters,
      onCancel: openIntroduceBoostersModal,
    });
  };

  const isContinueDisabled = !Object.values(selectedBoosters).some(Boolean);

  return (
    <Modal size="xs" open onClose={onClose}>
      <ModalContent>
        <Stack alignItems="center" flexDirection="row" justifyContent="space-between" mb={16}>
          <Typography color="grey.900" variant={isMobile ? 'title-4-md' : 'title-3-md'}>
            Add boosters for best results
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Stack>
          {/* Banner Image */}
          <Box
            sx={{
              borderRadius: '12px',
              height: 'auto',
              mb: 20,
              objectFit: 'cover',
              width: '100%',
            }}
            alt="Boosters Banner"
            component="img"
            src={BoostersBanner}
          />

          {/* Description */}
          <Stack gap={8} mb={8}>
            <Typography color="grey.900" variant="body-2">
              Boost your work without changing your plan — choose the tools you want to add to your workflow.
            </Typography>
            <Stack flexDirection="row" gap={4}>
              <Box
                component="a"
                href="https://help.rask.ai/hc/introducing-boosters"
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
                target="_blank"
              >
                <Typography color="primary.700" component="span" variant="body-2-md">
                  Learn more
                </Typography>
              </Box>
              <Typography color="grey.900" variant="body-2">
                about boosters
              </Typography>
            </Stack>
          </Stack>

          {/* Boosters List */}
          <Stack gap={8}>
            {allBoosters?.srt && (
              <BoosterItem
                title="SRT Upload & Download"
                description="Upload and download corrected transcript"
                isChecked={selectedBoosters[BoosterType.SRT]}
                price={`${formatBoosterPrice(allBoosters, BoosterType.SRT)} /month`}
                type={BoosterType.SRT}
                onChange={() => toggleBooster(BoosterType.SRT)}
              />
            )}

            {allBoosters?.[BoosterType.TranslationPrompting] && (
              <BoosterItem
                title="Translation prompting"
                description="Match your dubbed videos your brand voice"
                isChecked={selectedBoosters[BoosterType.TranslationPrompting]}
                price={`${formatBoosterPrice(allBoosters, BoosterType.TranslationPrompting)} /month`}
                type={BoosterType.TranslationPrompting}
                onChange={() => toggleBooster(BoosterType.TranslationPrompting)}
              />
            )}

            {allBoosters?.[BoosterType.TeamspaceSeat] && (
              <BoosterItem
                title="Extra seat for Teamspaces"
                rightContent={
                  selectedBoosters[BoosterType.TeamspaceSeat] ? (
                    <BoosterSeatsCounter
                      seatsCount={seatsCount}
                      onDecrement={decrementSeats}
                      onIncrement={incrementSeats}
                    />
                  ) : null
                }
                description="Collaborate with your team on shared projects seamlessly"
                isChecked={selectedBoosters[BoosterType.TeamspaceSeat]}
                price={`${formatBoosterPrice(allBoosters, BoosterType.TeamspaceSeat, seatsCount)} /month`}
                type={BoosterType.TeamspaceSeat}
                onChange={() => toggleBooster(BoosterType.TeamspaceSeat)}
              />
            )}
          </Stack>
        </Stack>
      </ModalContent>
      <ModalActions>
        <Button disabled={isContinueDisabled} size="lg" fullWidth onClick={handleAddAndContinue}>
          Add and continue
        </Button>
      </ModalActions>
    </Modal>
  );
};

export const IntroduceBoostersModal = () => (
  <ModalWrapper hook={useIntroduceBoostersModalStore}>
    <IntroduceBoostersModalComponent />
  </ModalWrapper>
);
